import { memo } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Grid, Link as MuiLink, Stack,
  ButtonBase as MuiButton,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  faSquareFacebook, faInstagram, faLinkedin, faSpotify,
  faTiktok, faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { useIsMobile } from '@client/hooks/dom.hooks';
import SideDoorLogo from '@client/images/logos/sidedoor_logo.svg';
import { baseUrl, newsUrl } from '@sda/utils/url';
import { FontAwesomeIcon } from '@client/components/common/FontAwesomeIcon';

const useStyles = makeStyles({ name: 'Footer' })((theme) => ({
  root: {
    backgroundColor: theme.palette.secondary.dark,

    [theme.breakpoints.down('md')]: {
      paddingBottom: theme.heights.BottomAppBar,
    },
  },
  container: {
    padding: theme.spacing(4, 2),

    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(6, 2),
      maxWidth: `calc(${theme.breakpoints.values.lg} + ${theme.spacing(2)})`,
    },
  },
  socialLinkContainer: {
    '& > a': {
      height: 20,
    },
  },
}));

export const FooterComponent = memo(() => {
  const { classes } = useStyles();
  const isMobile = useIsMobile();

  return (
    <footer className={classes.root}>
      <Container
        className={classes.container}
        maxWidth="lg"
      >
        <div>
          <Grid
            container
            spacing={3}
            justifyContent={isMobile ? 'flex-start' : 'space-between'}
            direction={isMobile ? 'column' : 'row'}
          >
            <Grid item>
              <MuiButton
                id="sda-footer-home-button"
                component={Link}
                to="/home"
                aria-label="Go to front page"
              >
                <img
                  src={SideDoorLogo}
                  alt="Side Door"
                  loading="lazy"
                  width={185}
                  height={43}
                />
              </MuiButton>
            </Grid>

            <Grid
              item
              container
              xs={12}
              sm={6}
            >
              <Grid
                item
                xs={6}
              >
                <Stack spacing={1}>
                  <MuiLink
                    id="sda-footer-pricing-link"
                    href={newsUrl('/pricing')}
                    variant="body2"
                    color="white"
                    underline="hover"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Pricing
                  </MuiLink>

                  <MuiLink
                    id="sda-footer-artists-hosts-link"
                    href={newsUrl('/host')}
                    variant="body2"
                    color="white"
                    underline="hover"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Host a Show
                  </MuiLink>

                  <MuiLink
                    id="sda-footer-saferspaces-link"
                    variant="body2"
                    color="white"
                    underline="hover"
                    target="_blank"
                    rel="noreferrer"
                    href={newsUrl('/safer-spaces')}
                  >
                    Safer Spaces
                  </MuiLink>

                  <MuiLink
                    id="sda-footer-knowledge-base-link"
                    variant="body2"
                    color="white"
                    underline="hover"
                    href={baseUrl('/support')}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Knowledge Base
                  </MuiLink>

                  <MuiLink
                    id="sda-footer-contact-support-link"
                    variant="body2"
                    color="white"
                    component={Link}
                    to="/contact-us"
                    underline="hover"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Contact Support
                  </MuiLink>

                  <MuiLink
                    id="sda-footer-news-link"
                    variant="body2"
                    color="white"
                    underline="hover"
                    href={newsUrl('/news')}
                    target="_blank"
                    rel="noreferrer"
                  >
                    News
                  </MuiLink>

                  <MuiLink
                    id="sda-footer-tos-link"
                    variant="body2"
                    color="white"
                    component={Link}
                    to="/terms-of-service"
                    underline="hover"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Legal
                  </MuiLink>

                  <MuiLink
                    id="sda-footer-privacy-policy-link"
                    variant="body2"
                    color="white"
                    component={Link}
                    to="/privacy-policy"
                    underline="hover"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </MuiLink>
                </Stack>
              </Grid>

              <Grid
                item
                xs={6}
              >
                <Stack
                  spacing={1}
                  className={classes.socialLinkContainer}
                >
                  <MuiLink
                    id="sda-footer-instagram-link"
                    variant="body1"
                    color="white"
                    underline="hover"
                    target="_blank"
                    rel="noreferrer"
                    href="https://www.instagram.com/sidedoor.access/?hl=en"
                  >
                    <FontAwesomeIcon
                      icon={faInstagram}
                      fontSize="small"
                    />

                    <span>Instagram</span>
                  </MuiLink>

                  <MuiLink
                    id="sda-footer-facebook-link"
                    variant="body1"
                    color="white"
                    underline="hover"
                    target="_blank"
                    rel="noreferrer"
                    href="https://www.facebook.com/sidedooraccess"
                  >
                    <FontAwesomeIcon
                      icon={faSquareFacebook}
                      fontSize="small"
                    />

                    <span>Facebook</span>
                  </MuiLink>

                  <MuiLink
                    id="sda-footer-youtube-link"
                    variant="body1"
                    color="white"
                    underline="hover"
                    target="_blank"
                    rel="noreferrer"
                    href="https://www.youtube.com/channel/UCRL4lWXOjlOVPPtC3pSsQYQ"
                  >
                    <FontAwesomeIcon
                      icon={faYoutube}
                      fontSize="small"
                    />

                    <span>Youtube</span>
                  </MuiLink>

                  <MuiLink
                    id="sda-footer-tiktok-link"
                    variant="body1"
                    color="white"
                    underline="hover"
                    target="_blank"
                    rel="noreferrer"
                    href="https://www.tiktok.com/@sidedooraccess?lang=en"
                  >
                    <FontAwesomeIcon
                      icon={faTiktok}
                      fontSize="small"
                    />

                    <span>TikTok</span>
                  </MuiLink>

                  <MuiLink
                    id="sda-footer-linkedin-link"
                    variant="body1"
                    color="white"
                    underline="hover"
                    target="_blank"
                    rel="noreferrer"
                    href="https://www.linkedin.com/company/side-door/"
                  >
                    <FontAwesomeIcon
                      icon={faLinkedin}
                      fontSize="small"
                    />

                    <span>LinkedIn</span>
                  </MuiLink>

                  <MuiLink
                    id="sda-footer-spotify-link"
                    variant="body1"
                    color="white"
                    underline="hover"
                    target="_blank"
                    rel="noreferrer"
                    href="https://open.spotify.com/user/sidedooraccess"
                  >
                    <FontAwesomeIcon
                      icon={faSpotify}
                      fontSize="small"
                    />

                    <span>Spotify</span>
                  </MuiLink>

                  <MuiLink
                    id="sda-footer-newsletter-link"
                    variant="body1"
                    color="white"
                    underline="hover"
                    target="_blank"
                    rel="noreferrer"
                    href="http://eepurl.com/hqNNrz"
                  >
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      fontSize="small"
                    />

                    <span>Newsletter</span>
                  </MuiLink>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </Container>
    </footer>
  );
});

export default FooterComponent;
